import { Button, Divider, Form, Input, message, Modal, Popconfirm, Select, Space, Switch, Table, Typography } from 'antd';
import { DeleteFilled, LogoutOutlined } from "@ant-design/icons";
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { addMember, logoutUserFromServices, removeMember, removeUserMFA, resetMemberLoginSecurity, resetPasswordUser, revertPasswordUser, updateStatusMember, updateUserInfo } from 'services';
import { convertDateTime } from 'utils';
import { getUserInfo } from '../../../../services/user';

interface DataType {
  key: string,
  email: string,
  firstName: string,
  lastName: string,
  permission: string,
  azinsightLastLogin: string,
  azinsightVersion: string,
  scanezLogin: string,
  scanezVersion: string,
  saveMasterDb: boolean,
  status: string,
  hiddenMarketplace: [],
  updated: string,
  totpEnabled?: boolean,
  isLocked?: boolean,
}

const SubAccountTab = ({ data }: any) => {
  const userId = data.user._id;

  const [dataTable, setDataTable] = useState<any>([]);
  const [openModalAddMember, setOpenModalAddMember] = useState<boolean>(false);
  const [loadingField, setLoadingField] = useState<boolean>(false);
  const [fieldUpdate, setFieldUpdate] = useState<string>('');
  const [loadingAddMember, setLoadingAddMember] = useState<boolean>(false);
  const [loadingSecurity, setLoadingSecurity] = useState<boolean>(false);
  const [loadingMfa, setLoadingMfa] = useState<boolean>(false);
  const [addMemberFields, setAddMemberFields] = useState<any>({
    email: undefined,
    firstName: undefined,
    lastName: undefined,
    extension: "AZINSIGHT",
    managerId: userId,
    mauticContactManagerId: data.user.mauticContactId,
  })

  const columns: ColumnsType<DataType> = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
    },
    {
      title: 'Permission',
      dataIndex: 'permission',
      key: 'permission',
    },
    {
      title: 'AZInsight Last Login',
      dataIndex: 'azinsightLastLogin',
      key: 'azinsightLastLogin',
    },
    {
      title: 'AZInsight Version',
      dataIndex: 'azinsightVersion',
      key: 'azinsightVersion',
    },
    {
      title: 'ScanEZ Login',
      dataIndex: 'scanezLogin',
      key: 'scanezLogin',
    },
    {
      title: 'ScanEZ Version',
      dataIndex: 'scanezVersion',
      key: 'scanezVersion',
    },
    {
      title: 'Save to MasterDB',
      dataIndex: 'saveMasterDb',
      key: 'saveMasterDb',
      render: (value, fields) =>
        <Select
          loading={fieldUpdate === fields.key && loadingField}
          onSelect={(value: any) => handleOnSelectField(value, 'allowSaveToMaster', fields.key)}
          value={value}
          options={[
            { label: 'True', value: true },
            { label: 'False', value: false },
          ]}
        />
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value, fields) => {
        return (
          <Switch
            loading={fieldUpdate === fields.key && loadingField}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
            checked={value === "active" || value === undefined}
            onChange={(value) => handleOnChangeStatus(value, fields.key)}
          />
        )
      }
    },
    {
      title: 'MFA Status',
      dataIndex: 'totpEnabled',
      key: 'totpEnabled',
      render: (value: boolean) => {
        return value ? (
          <Typography.Text type="success">Enabled</Typography.Text>
        ) : (
          <Typography.Text type="secondary">Disabled</Typography.Text>
        )
      }
    },
    {
      title: 'Lock Status',
      dataIndex: 'isLocked',
      key: 'isLocked',
      render: (value: boolean) => {
        return value ? (
          <Typography.Text type="danger">Locked</Typography.Text>
        ) : (
          <Typography.Text type="success">Unlocked</Typography.Text>
        )
      }
    },
    {
      title: 'Hidden Marketplace',
      dataIndex: 'hiddenMarketplace',
      key: 'hiddenMarketplace',
    },
    {
      title: 'Updated',
      dataIndex: 'updated',
      key: 'updated',
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_: string, record: any) => {
        return (
          <Space>
            <Popconfirm
              placement="topRight"
              title="Are you sure to want to logout this member?"
              onConfirm={() => handleLogoutClick(record.key)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary" icon={<LogoutOutlined />} disabled={fieldUpdate === record.key && loadingField} />
            </Popconfirm>
            <Popconfirm
              placement="topRight"
              title="Are you sure to delete this one?"
              onConfirm={() => handleOnDeleteMember(record.key)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary" icon={<DeleteFilled />} danger disabled={fieldUpdate === record.key && loadingField} />
            </Popconfirm>
            {record.originPassword ? (
              <Popconfirm
                placement="topRight"
                title="Password is already reset to default, this action will revert it back to original password"
                onConfirm={() => handleOnRevertPassword(record.key)}
                okText="Yes"
                cancelText="No"
              >
                <Button type="primary" disabled={fieldUpdate === record.key && loadingField}>Revert</Button>
              </Popconfirm>
            ) : (
              <Popconfirm
                placement="topRight"
                title="Are you sure to change password?"
                onConfirm={() => handleOnResetPassword(record.key)}
                okText="Yes"
                cancelText="No"
              >
                <Button type="primary" danger disabled={fieldUpdate === record.key && loadingField}>Password Temp Change</Button>
              </Popconfirm>
            )}
            <Popconfirm
              placement="topRight"
              title="Are you sure to change password?"
              onConfirm={() => handleOnResetPassword(record.key, false)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary" danger disabled={fieldUpdate === record.key && loadingField}>Password change</Button>
            </Popconfirm>
            {/* Only show unlock button if account is locked */}
            {record.isLocked && (
              <Popconfirm
                placement="topRight"
                title="Reset Login Counters & Unlock"
                description="This will reset failed login attempts, unlock the account, and clear cooldown. Continue?"
                onConfirm={() => handleResetMemberLoginSecurity(record.key)}
                okText="Yes, Reset"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Button
                  type="primary"
                  loading={fieldUpdate === record.key && loadingSecurity}
                  size="small"
                  disabled={fieldUpdate === record.key && (loadingField || loadingSecurity)}
                >
                  Reset Login Counters & Unlock
                </Button>
              </Popconfirm>
            )}

            {/* Only show remove MFA button if MFA is enabled */}
            {record.totpEnabled && (
              <Popconfirm
                placement="topRight"
                title="Remove MFA for this member?"
                description="This will disable TOTP (MFA) and clear recovery codes. Continue?"
                onConfirm={() => handleRemoveMemberMFA(record.key)}
                okText="Yes, Remove"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Button
                  danger
                  type="primary"
                  size="small"
                  loading={fieldUpdate === record.key && loadingMfa}
                  disabled={fieldUpdate === record.key && (loadingField || loadingSecurity || loadingMfa)}
                >
                  Remove MFA
                </Button>
              </Popconfirm>
            )}
          </Space>
        )
      }
    },
  ];

  const handleRemoveMemberMFA = (id: string) => {
    setFieldUpdate(id);
    setLoadingMfa(true);
    removeUserMFA(id)
      .then(() => {
        message.success('Removed MFA successfully');
        // Update local state to reflect MFA removal
        setDataTable((prevState: any) => {
          return prevState.map((item: any) => {
            if (item.key === id) {
              return {
                ...item,
                totpEnabled: false,
              }
            }
            return item;
          });
        });
      })
      .catch(() => {
        message.error("Couldn't remove MFA");
      })
      .finally(() => {
        setLoadingMfa(false);
      })
  }

  const handleOnValuesChange = (changedValues: any, allValues: any) => {
    setAddMemberFields((prevState: any) => ({
      ...allValues,
      managerId: prevState.managerId,
      mauticContactManagerId: prevState.mauticContactManagerId,
    }))
  };

  const handleOnAddMember = () => {
    setLoadingAddMember(true);
    setOpenModalAddMember(false);
    addMember(addMemberFields)
      .then(() => {
        message.success('Added Member successfully');
        getUserInfo(addMemberFields.email).then(({ data: res }) => {
          setDataTable((prevState: any) => {
            const newMember = {
              key: res.user._id,
              email: res.user.email,
              firstName: res.user.firstName,
              lastName: res.user.lastName,
              permission: Object.keys(res.user.permission).join(', '),
              azinsightLastLogin: convertDateTime(res.user.lastLoginAt),
              azinsightVersion: res.user.azinsightVersion,
              scanezLogin: convertDateTime(res.user.mobileLastTimeLogin),
              scanezVersion: res.user.mobileLastTimeLoginVersion,
              saveMasterDb: res.user.allowSaveToMaster,
              status: res.user.status,
              hiddenMarketplace: Object.keys(res.user.hideMarketplace).length > 0 && Object.keys(res.user.hideMarketplace).join(', '),
              updated: convertDateTime(res.user.updatedAt),
              originPassword: res.user.origin_password?.useDefaultPassword ? true : false,
              totpEnabled: res.user.totpEnabled || false,
              isLocked: res.user.isLocked || false,
            }
            setLoadingAddMember(false);
            return [
              newMember,
              ...prevState,
            ]
          })
        })
      })
      .catch(() => {
        message.success("Couldn't add member successfully");
        setLoadingAddMember(false);
      })
  };

  const handleOnDeleteMember = (id: string) => {
    setFieldUpdate(id);
    setLoadingField(true);
    
    // Find member in dataTable to get email
    const member = dataTable.find((item: any) => item.key === id);
    if (!member || !member.email) {
      message.error("Cannot find member email");
      setLoadingField(false);
      return;
    }
    
    removeMember(member.email)
      .then(() => {
        message.success('Deleted member!!');
        setDataTable((prevState: any) => {
          const newData = prevState.filter((item: any) => item.key !== id);
          setLoadingField(false);
          return newData;
        })
      })
      .catch(() => {
        message.error("Couldn't delete member successfully");
        setLoadingField(false);
      })
  };

  const handleLogoutClick = (id: string) => {
    setFieldUpdate(id);
    setLoadingField(true);
    const params = {
      id,
      type: 'ALL',
    }
    logoutUserFromServices(params)
      .then(() => {
        message.success('Logged out member!!');
        setLoadingField(false);
      })
  };

  const handleOnResetPassword = (id: string, temp: boolean = true) => {
    setFieldUpdate(id);
    setLoadingField(true);

    const params = { temp };
    resetPasswordUser(id, { params })
      .then(({ data: res }) => {
        setDataTable((prevState: any) => {
          const newData = prevState.map((item: any) => {
            if (item.key === id) {
              return ({
                ...item,
                originPassword: temp || false
              })
            }
            return item;
          });
          setLoadingField(false);
          return newData;
        });
        message.success("Reset member's password successfully");
      })
      .catch(() => {
        message.success("Couldn't reset member's password successfully");
        setLoadingField(false);
      })
  }

  const handleOnRevertPassword = (id: string) => {
    setFieldUpdate(id);
    setLoadingField(true);

    revertPasswordUser(id)
      .then(({ data: res }) => {
        setDataTable((prevState: any) => {
          const newData = prevState.map((item: any) => {
            if (item.key === id) {
              return ({
                ...item,
                value: res.data,
                originPassword: false
              })
            }
            return item;
          });
          setLoadingField(false);
          return newData;
        });
        message.success("Reverted member's password successfully")
      })
      .catch(() => {
        message.success("Couldn't revert member's password successfully");
        setLoadingField(false);
      })
  }

  const handleResetMemberLoginSecurity = (id: string) => {
    setFieldUpdate(id);
    setLoadingSecurity(true);
    resetMemberLoginSecurity(id)
      .then(() => {
        message.success("Member login security reset successfully");
        // Update local state to reflect unlock
        setDataTable((prevState: any) => {
          return prevState.map((item: any) => {
            if (item.key === id) {
              return {
                ...item,
                isLocked: false,
              }
            }
            return item;
          });
        });
        setLoadingSecurity(false);
        setFieldUpdate('');
      })
      .catch(() => {
        message.error("Failed to reset member login security");
        setLoadingSecurity(false);
        setFieldUpdate('');
      })
  }

  const handleOnSelectField = (value: any, field: string, member: string) => {
    setFieldUpdate(member);
    setLoadingField(true);

    const valueUpdate = value ? '1' : '0';
    const params = {
      value: valueUpdate,
      field,
      id: member
    };
    updateUserInfo(params)
      .then(() => {
        setDataTable((prevState: any) => {
          const newData = prevState.map((item: any) => {
            if (item.key === member) {
              return ({
                ...item,
                saveMasterDb: value
              })
            }
            return item;
          });

          setLoadingField(false);
          return newData;
        })
        message.success("Saved successfully")
      })
      .catch(() => {
        message.success("Couldn't save successfully");
        setLoadingField(false);
      })
  }

  const handleOnChangeStatus = (value: any, member: string) => {
    setFieldUpdate(member);
    setLoadingField(true);
    const valueUpdate = value ? '1' : '0';
    const params = {
      value: valueUpdate,
      id: member
    };
    updateStatusMember(params)
      .then(() => {
        setDataTable((prevState: any) => {
          const newData = prevState.map((item: any) => {
            if (item.key === member) {
              return ({
                ...item,
                status: value ? "active" : "inactive"
              })
            }
            return item;
          });

          setLoadingField(false);
          return newData;
        });
        message.success("Saved successfully")
      })
      .catch(() => {
        message.success("Couldn't save successfully");
        setLoadingField(false);
      })
  }

  const loadSubAccounts = () => {
    const tableList = data.members.map((member: any) => {
      const idMember = member._id;
      const hiddenMarketplace =
        member.hideMarketplace && Object.keys(member.hideMarketplace).length > 0 &&
        Object.keys(member.hideMarketplace).filter((mkp: string) => member.hideMarketplace[mkp]).join(', ')
      return {
        key: idMember,
        email: member.email,
        firstName: member.firstName,
        lastName: member.lastName,
        permission: Object.keys(member.permission).join(', '),
        azinsightLastLogin: convertDateTime(member.lastLoginAt),
        azinsightVersion: member.azinsightVersion,
        scanezLogin: convertDateTime(member.mobileLastTimeLogin),
        scanezVersion: member.mobileLastTimeLoginVersion,
        saveMasterDb: member.allowSaveToMaster,
        status: member.status,
        hiddenMarketplace,
        updated: convertDateTime(member.updatedAt),
        originPassword: member.origin_password?.useDefaultPassword ? true : false,
        totpEnabled: member.totpEnabled || false,
        isLocked: member.isLocked || false,
      }
    });
    setDataTable(() => {
      return (tableList)
    })
  }

  useEffect(() => {
    loadSubAccounts();
  }, [])

  return (
    <>
      <Space direction="vertical" size="large" style={{ width: "100%", padding: 20 }}>
        <Divider orientation="left"><Typography.Title level={3}>User Sub-Accounts</Typography.Title></Divider>
        <Button type="primary" onClick={() => setOpenModalAddMember(true)} loading={loadingAddMember}>Add Member</Button>
        <Table
          loading={loadingAddMember}
          columns={columns}
          dataSource={dataTable}
          scroll={{ x: true }}
        />
      </Space>
      <Modal
        title="Add new member"
        visible={openModalAddMember}
        onOk={handleOnAddMember}
        onCancel={() => {
          setOpenModalAddMember(false);
        }}
        okText="Add"
      >
        <Form
          name="basic"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          autoComplete="off"
          onValuesChange={handleOnValuesChange}
        >
          <Form.Item
            label="Email"
            name="email"
            labelAlign="left"
            rules={[{ required: true, message: 'Please input your email!' }]}
          >
            <Input value={addMemberFields.email} />
          </Form.Item>
          <Form.Item
            label="First name"
            name="firstName"
            labelAlign="left"
            rules={[{ required: true, message: 'Please input your First name!' }]}
          >
            <Input value={addMemberFields.firstName} />
          </Form.Item>
          <Form.Item
            label="Last name"
            name="lastName"
            labelAlign="left"
            rules={[{ required: true, message: 'Please input your Last name!' }]}
          >
            <Input value={addMemberFields.lastName} />
          </Form.Item>
          <Form.Item
            label="Extension"
            name="extension"
            labelAlign="left"
            rules={[{ required: true, message: 'Please select your Extension!' }]}
          >
            <Select
              value={addMemberFields.extension}
              options={[
                { label: 'AZInsight', value: 'AZINSIGHT' },
                { label: 'AZAlert', value: 'AZALERT' },
              ]}
            />
          </Form.Item>
          <Form.Item
            hidden
            name="managerId"
          >
            <Input value={addMemberFields.managerId} />
          </Form.Item>
          <Form.Item
            hidden
            name="mauticContactManagerId"
          >
            <Input value={addMemberFields.mauticContactId} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default SubAccountTab;